import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';

import { environment } from '../../../environments';
import { Service, UploadOverallTracker } from '../../models';
import { SnackbarService } from './snackbar.service';

@Injectable()
export class JobUploaderService {
  private serviceUploaderSource = new BehaviorSubject<boolean>(false);
  currentJobUploader = this.serviceUploaderSource.asObservable();
  private selectedUploaderService: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private currentUploads: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private _activeUploads$: BehaviorSubject<{
    [uploadUid: string]: XMLHttpRequest
  }> = new BehaviorSubject<{
    [uploadUid: string]: XMLHttpRequest
  }>({});

  onlineEvent: Observable<Event>;
  offlineEvent: Observable<Event>;

  constructor(private http: HttpClient, private snackbarService: SnackbarService) {
    this.onlineEvent = fromEvent(window, 'online');

    this.onlineEvent
      .subscribe(ev => {
        const activeRequests = Object.values(this._activeUploads$.value);

        if (activeRequests.length) {
          this.snackbarService.showSnackbar('Connection restored! Please retry failed uploads.');
        } else {
          this.snackbarService.showSnackbar('Connection restored!');
        }
      });

    this.offlineEvent = fromEvent(window, 'offline');

    this.offlineEvent
      .subscribe(ev => {
        const activeRequests = Object.values(this._activeUploads$.value);

        if (activeRequests.length) {
          this.snackbarService.showSnackbar('Connection lost. Uploads will fail until connection is restored.');
        } else {
          this.snackbarService.showSnackbar('Seems you are offline. Please check your connection.');
        }
      });
  }

  deleteUploads(uploadsToDelete) {
    return this.http.post(`${environment.apiBaseUrl}/processor2-delete-uploads`, {uploadsToDelete});
  }

  getPresignedUploadUrl(fileMetadata: {
    fileName: string;
    contentType: string;
    contentLength: number;
    serviceId: string;
    processorUid: string;
  }) {
    return this.http.post<{
      data: {
        presignedUrl: string;
        filePath: string;
      }
    }>(`${environment.apiBaseUrl}/processor2-get-upload-presigned-url`, fileMetadata);
  }

  getThumbUrls(thumbPaths: Array<{thumbName: string; thumbPath: string; thumbUUID: string}>) {
    return this.http.post<{
      code: number;
      data: {
        thumbUrls: Array<{
          thumbUUID: string;
          url: string;
          thumbName: string;
        }>
      }
    }>(`${environment.apiBaseUrl}/processor2-get-thumb-urls`, { thumbPaths });
  }

  uploadToPresignedUrl(
    presignedUrl: string, 
    file: File, 
    processorUid: string,
    onProgress?: (progress: number) => void,
    uploadUUID?: string
  ): Promise<void> {
    // Use native fetch API for proper header control with presigned URLs
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track this upload
      if (uploadUUID) {
        this._activeUploads$.value[uploadUUID] = xhr;
      }

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      // Success handler
      xhr.addEventListener('load', () => {
        if (uploadUUID) {
          delete this._activeUploads$.value[uploadUUID];
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // Error handler
      xhr.addEventListener('error', () => {
        if (uploadUUID) {
          delete this._activeUploads$.value[uploadUUID];
        }
        reject(new Error('Upload failed'));
      });

      // Abort handler
      xhr.addEventListener('abort', () => {
        if (uploadUUID) {
          delete this._activeUploads$.value[uploadUUID];
        }
        reject(new Error('Upload aborted'));
      });

      // Open and configure request
      xhr.open('PUT', presignedUrl);
      
      // Set headers - these will NOT be overridden by XMLHttpRequest
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.setRequestHeader('x-goog-meta-processingStatus', 'process');
      xhr.setRequestHeader('x-goog-meta-fileUUID', processorUid);

      // Send the file
      xhr.send(file);
    });
  }

  get currentUploads$(): Observable<UploadOverallTracker> {
    return this.currentUploads.asObservable();
  }

  setSelectedUploaderService(service) {
    this.selectedUploaderService.next(service);
  }

  setCurrentUpload(uploadTask: any, fileUploadProgress: number, fileUploadCompleted: boolean, uploadName: string, uploadUUID: string, jobId: string) {
    const currentUploadsTemp: any = this.currentUploads.getValue();

    if (!!uploadUUID && !!jobId) {
      if (!currentUploadsTemp[jobId]) {
        currentUploadsTemp[jobId] = {};
        currentUploadsTemp[jobId][uploadUUID] = {
          fileName: uploadName,
          progress: fileUploadProgress,
          finalized: fileUploadCompleted
        };
      } else {
        currentUploadsTemp[jobId][uploadUUID] = {
          fileName: uploadName,
          progress: fileUploadProgress,
          finalized: fileUploadCompleted
        };

        if (fileUploadCompleted) {
          delete currentUploadsTemp[jobId][uploadUUID];

          if (Object.values(currentUploadsTemp[jobId]).length === 0) {
            delete currentUploadsTemp[jobId];
          }
        }
      }

      // uploadTask parameter is deprecated and kept for backward compatibility
      // Upload tracking is now handled internally by uploadToPresignedUrl using HttpClient
      this.currentUploads.next(currentUploadsTemp);
    }
  }

  deliverService(service) {
    return this.http.post(environment.apiBaseUrl + '/processor2-deliver-service', service)
      .toPromise();
  }

  updateServiceUploaded(value: boolean) {
    this.serviceUploaderSource.next(value);
  }

}
